import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, FORM_CONTROL_CLASS, FORM_LABEL_CLASS, FORM_TEXTAREA_CLASS, Modal, TABLE_HEADER_CELL_CLASS, type BadgeTone } from '@safetyhub/ui';
import {
  CHEMICAL_LINK_ACTION_LABEL,
  CHEMICAL_SUBSTANCE_KIND_LABEL,
  REGULATORY_LIST_ENTRY_LINK_STATUS_LABEL,
  formatDateTimeISO,
  type AdminChemicalEntryView,
  type AdminChemicalHazardView,
  type AdminChemicalSubstanceView,
  type RegulatoryListEntryLinkStatusValue,
} from '@safetyhub/shared';
import { extractApiError } from '../lib/api';
import { apiErrorMessage } from '../lib/apiErrorMessage';
import { CHEMICAL_LINKS_QUERY_KEYS, chemicalLinksApi } from '../lib/chemicalLinks';
import { useToastStore } from '../stores/toast';

/**
 * **화학물질 연결(국소배기 49종)** — 49종 목록 항목 ↔ 물질 마스터 ↔ 별표 21 항목의 연결을 고치는 화면(사용자 결정 2026-11-02 2차 ② ·
 * 설계 `docs/plans/2026-11-02-chemical-substance-master-design.md` §1-2 ⑥). 서버 `AdminChemicalLinksController` · 쓰기는 `ChemicalLinkService` 하나.
 *
 * ## 무엇을 바꾸면 무엇이 달라지나
 * 국소배기장치 안전검사의 「최근 2년 노출기준 50% 미만 제외」 판정은 **연결 행**을 읽는다 — 여기서 연결을 더하면 배포 없이 다음 판정부터
 * 반영된다. 항목 상태(연결됨·미확정·미검토)는 사람이 읽는 칸이고 판정은 보지 않는다(미확정 = 연결 0 = 확인 필요).
 * 연결·상태 변경마다 **근거(원문 문구)·사유**를 받고 이력에 남긴다(누가·언제·무엇을·왜).
 *
 * ## 이 화면이 하지 않는 것
 * 물질 마스터 행 자체(이름·CAS)·묶음 멤버십의 편집 — 그 입구는 아직 없다(마이그레이션 seed · 2차). 「상위 묶음」으로 풀리는 연결은 멤버십이
 * 정하므로, 연결을 걸 때 물질을 고르면 그 물질이 속한 묶음의 별표 21 항목까지 「판정이 보는 항목」에 보인다.
 */
const STATUS_TONE: Record<RegulatoryListEntryLinkStatusValue, BadgeTone> = { LINKED: 'blue', UNRESOLVED: 'amber', UNREVIEWED: 'slate' };

type ModalState =
  | { kind: 'addEntry'; entry: AdminChemicalEntryView }
  | { kind: 'removeEntry'; entry: AdminChemicalEntryView; substanceId: string }
  | { kind: 'status'; entry: AdminChemicalEntryView }
  | { kind: 'history'; itemCode: string | null; hazardCode: string | null; title: string }
  | { kind: 'addHazard'; hazard: AdminChemicalHazardView }
  | { kind: 'removeHazard'; hazard: AdminChemicalHazardView; substanceId: string };

function substanceLabel(s: AdminChemicalSubstanceView): string {
  const cas = s.casNumbers.length > 0 ? ` [${s.casNumbers.join(', ')}]` : '';
  return `${s.nameKo}${cas} · ${CHEMICAL_SUBSTANCE_KIND_LABEL[s.kind]}`;
}

function SubstanceSelect({ substances, value, onChange }: { substances: readonly AdminChemicalSubstanceView[]; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className={FORM_LABEL_CLASS}>물질(마스터)</span>
      <select className={FORM_CONTROL_CLASS} value={value} onChange={(e) => onChange(e.target.value)} aria-label="물질">
        <option value="">선택</option>
        {substances.map((s) => (
          <option key={s.id} value={s.id}>
            {substanceLabel(s)}
          </option>
        ))}
      </select>
    </label>
  );
}

function TextArea({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label className="block">
      <span className={FORM_LABEL_CLASS}>{label}</span>
      <textarea className={FORM_TEXTAREA_CLASS} rows={3} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={label} />
    </label>
  );
}

export default function ChemicalLinks() {
  const queryClient = useQueryClient();
  const { showToast } = useToastStore();
  const { data, isLoading, error } = useQuery({ queryKey: CHEMICAL_LINKS_QUERY_KEYS.all, queryFn: () => chemicalLinksApi.get() });
  const [modal, setModal] = useState<ModalState | null>(null);
  const [hazardFilter, setHazardFilter] = useState('');
  const [showUnlinkedHazards, setShowUnlinkedHazards] = useState(false);

  // 모달 입력 — 모달마다 쓰는 칸만 쓴다(열 때 비운다)
  const [substanceId, setSubstanceId] = useState('');
  const [basisNote, setBasisNote] = useState('');
  const [reason, setReason] = useState('');
  const [nextStatus, setNextStatus] = useState<RegulatoryListEntryLinkStatusValue>('UNRESOLVED');
  const [note, setNote] = useState('');

  const open = (next: ModalState) => {
    setSubstanceId('');
    setBasisNote('');
    setReason('');
    setNote(next.kind === 'status' ? (next.entry.linkNote ?? '') : '');
    setNextStatus(next.kind === 'status' ? next.entry.linkStatus : 'UNRESOLVED');
    setModal(next);
  };
  const close = () => setModal(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: CHEMICAL_LINKS_QUERY_KEYS.all });
  const onError = (err: unknown) => showToast(extractApiError(err).message, 'error');
  const done = (message: string) => async () => {
    showToast(message, 'success');
    await refresh();
    close();
  };

  const addEntry = useMutation({
    mutationFn: (m: Extract<ModalState, { kind: 'addEntry' }>) => chemicalLinksApi.addEntryLink(m.entry.itemCode, { substanceId, basisNote, reason }),
    onSuccess: done('연결을 추가했습니다.'),
    onError,
  });
  const removeEntry = useMutation({
    mutationFn: (m: Extract<ModalState, { kind: 'removeEntry' }>) =>
      chemicalLinksApi.removeEntryLink(m.entry.itemCode, m.substanceId, {
        reason,
        ...(m.entry.links.length <= 1 ? { nextStatus: nextStatus === 'LINKED' ? 'UNRESOLVED' : nextStatus, note: nextStatus === 'UNRESOLVED' ? note : null } : {}),
      }),
    onSuccess: done('연결을 해제했습니다.'),
    onError,
  });
  const setStatus = useMutation({
    mutationFn: (m: Extract<ModalState, { kind: 'status' }>) =>
      chemicalLinksApi.setEntryStatus(m.entry.itemCode, { status: nextStatus, note: nextStatus === 'UNRESOLVED' ? note : null, reason }),
    onSuccess: done('상태를 바꿨습니다.'),
    onError,
  });
  const addHazard = useMutation({
    mutationFn: (m: Extract<ModalState, { kind: 'addHazard' }>) => chemicalLinksApi.addHazardLink(m.hazard.code, { substanceId, basisNote, reason }),
    onSuccess: done('별표 21 연결을 추가했습니다.'),
    onError,
  });
  const removeHazard = useMutation({
    mutationFn: (m: Extract<ModalState, { kind: 'removeHazard' }>) => chemicalLinksApi.removeHazardLink(m.hazard.code, m.substanceId, { reason }),
    onSuccess: done('별표 21 연결을 해제했습니다.'),
    onError,
  });
  const historyQuery = useQuery({
    queryKey: CHEMICAL_LINKS_QUERY_KEYS.history(modal?.kind === 'history' ? modal.itemCode : null, modal?.kind === 'history' ? modal.hazardCode : null),
    queryFn: () =>
      chemicalLinksApi.history({
        itemCode: modal?.kind === 'history' ? (modal.itemCode ?? undefined) : undefined,
        hazardCode: modal?.kind === 'history' ? (modal.hazardCode ?? undefined) : undefined,
      }),
    enabled: modal?.kind === 'history',
  });

  const substances = data?.substances ?? [];
  const substanceById = useMemo(() => new Map(substances.map((s) => [s.id, s])), [substances]);
  const hazardNameByCode = useMemo(() => new Map((data?.hazards ?? []).map((h) => [h.code, h.name.replace(/[([].*$/, '').trim()])), [data]);
  const visibleHazards = useMemo(() => {
    const q = hazardFilter.trim();
    return (data?.hazards ?? []).filter((h) => (showUnlinkedHazards || h.links.length > 0) && (q === '' || h.code.includes(q) || h.name.includes(q)));
  }, [data, hazardFilter, showUnlinkedHazards]);
  const counts = useMemo(() => {
    const c = { LINKED: 0, UNRESOLVED: 0, UNREVIEWED: 0 };
    for (const e of data?.entries ?? []) c[e.linkStatus] += 1;
    return c;
  }, [data]);
  const busy = addEntry.isPending || removeEntry.isPending || setStatus.isPending || addHazard.isPending || removeHazard.isPending;

  if (isLoading) return <p className="p-6 text-sm text-ink-500">불러오는 중…</p>;
  if (error || !data) {
    return (
      <p role="alert" className="p-6 text-sm text-rose-700">
        {error ? extractApiError(error).message : '연결 목록을 불러오지 못했습니다.'}
      </p>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-xl font-bold text-ink-900">화학물질 연결 — {data.list.name}</h1>
        <p className="mt-1 text-sm text-ink-500">
          {data.list.sourceLocator} · 목록 version {data.list.version}. 연결됨 {counts.LINKED} · 미확정 {counts.UNRESOLVED} · 미검토 {counts.UNREVIEWED}.
          국소배기장치 안전검사 제외 판정은 <strong>연결 행</strong>을 읽습니다 — 연결을 더하면 배포 없이 다음 판정부터 반영됩니다. 미확정·미검토 항목은 판정이
          「확인 필요」(측정 연결 없음)로 섭니다.
        </p>
      </div>

      <Card padding="md" radius="lg" elevation="sm">
        <h2 className="text-base font-semibold text-ink-900">49종 항목 ↔ 물질</h2>
        <table className="mt-3 w-full text-sm" data-testid="chemical-entries">
          <thead>
            <tr>
              <th className={TABLE_HEADER_CELL_CLASS}>코드</th>
              <th className={TABLE_HEADER_CELL_CLASS}>고시 표기</th>
              <th className={TABLE_HEADER_CELL_CLASS}>상태</th>
              <th className={TABLE_HEADER_CELL_CLASS}>연결된 물질(근거)</th>
              <th className={TABLE_HEADER_CELL_CLASS}>판정이 보는 별표 21</th>
              <th className={TABLE_HEADER_CELL_CLASS}></th>
            </tr>
          </thead>
          <tbody>
            {data.entries.map((e) => (
              <tr key={e.itemCode} className="border-t border-line align-top" data-item-code={e.itemCode}>
                <td className="px-2 py-2 font-mono text-xs">{e.itemCode}</td>
                <td className="px-2 py-2">{e.text}</td>
                <td className="px-2 py-2">
                  <Badge tone={STATUS_TONE[e.linkStatus]} shape="rounded">
                    {REGULATORY_LIST_ENTRY_LINK_STATUS_LABEL[e.linkStatus]}
                  </Badge>
                  {e.linkNote && <p className="mt-1 max-w-md text-xs text-ink-500">{e.linkNote}</p>}
                </td>
                <td className="px-2 py-2">
                  {e.links.length === 0 ? (
                    <span className="text-xs text-ink-400">없음</span>
                  ) : (
                    <ul className="space-y-1">
                      {e.links.map((l) => (
                        <li key={l.substanceId} className="flex flex-wrap items-start gap-1">
                          <span title={l.basisNote}>{substanceLabel(l.substance)}</span>
                          <Button variant="ghost" size="sm" disabled={busy} onClick={() => open({ kind: 'removeEntry', entry: e, substanceId: l.substanceId })}>
                            해제
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
                <td className="px-2 py-2 font-mono text-xs">{e.relevantHazardCodes.length > 0 ? e.relevantHazardCodes.map((c) => `${c} ${hazardNameByCode.get(c) ?? ''}`).join(' · ') : '—'}</td>
                <td className="px-2 py-2 whitespace-nowrap">
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => open({ kind: 'addEntry', entry: e })}>
                    연결 추가
                  </Button>
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => open({ kind: 'status', entry: e })}>
                    상태
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => open({ kind: 'history', itemCode: e.itemCode, hazardCode: null, title: `${e.itemCode} ${e.text} 이력` })}>
                    이력
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card padding="md" radius="lg" elevation="sm">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-base font-semibold text-ink-900">별표 21 항목 ↔ 물질</h2>
          <input className={FORM_CONTROL_CLASS + ' max-w-xs'} placeholder="코드·이름으로 찾기" value={hazardFilter} onChange={(e) => setHazardFilter(e.target.value)} aria-label="별표 21 찾기" />
          <label className="flex items-center gap-1 text-xs text-ink-600">
            <input type="checkbox" checked={showUnlinkedHazards} onChange={(e) => setShowUnlinkedHazards(e.target.checked)} /> 연결 없는 항목도 보기
          </label>
        </div>
        <ul className="mt-3 divide-y divide-line text-sm" data-testid="chemical-hazards">
          {visibleHazards.map((h) => (
            <li key={h.code} className="flex flex-wrap items-start gap-2 py-2" data-hazard-code={h.code}>
              <span className="w-24 shrink-0 font-mono text-xs">{h.code}</span>
              <span className="min-w-0 flex-1">{h.name}</span>
              <span className="flex flex-wrap items-center gap-1">
                {h.links.map((l) => (
                  <span key={l.substanceId} className="inline-flex items-center gap-1">
                    <Badge tone="slate" shape="rounded">
                      <span title={l.basisNote}>{l.substance.nameKo}</span>
                    </Badge>
                    <Button variant="ghost" size="sm" disabled={busy} onClick={() => open({ kind: 'removeHazard', hazard: h, substanceId: l.substanceId })}>
                      해제
                    </Button>
                  </span>
                ))}
                <Button variant="ghost" size="sm" disabled={busy} onClick={() => open({ kind: 'addHazard', hazard: h })}>
                  연결 추가
                </Button>
                <Button variant="ghost" size="sm" onClick={() => open({ kind: 'history', itemCode: null, hazardCode: h.code, title: `${h.code} 이력` })}>
                  이력
                </Button>
              </span>
            </li>
          ))}
        </ul>
      </Card>

      {/* 연결 추가(49종 · 별표 21) */}
      <Modal
        open={modal?.kind === 'addEntry' || modal?.kind === 'addHazard'}
        onClose={close}
        title={modal?.kind === 'addEntry' ? `${modal.entry.itemCode} ${modal.entry.text} — 물질 연결 추가` : modal?.kind === 'addHazard' ? `${modal.hazard.code} — 물질 연결 추가` : ''}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={busy}>
              취소
            </Button>
            <Button
              variant="primary"
              disabled={busy || !substanceId || !basisNote.trim() || !reason.trim()}
              onClick={() => (modal?.kind === 'addEntry' ? addEntry.mutate(modal) : modal?.kind === 'addHazard' ? addHazard.mutate(modal) : undefined)}
            >
              추가
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <SubstanceSelect substances={substances} value={substanceId} onChange={setSubstanceId} />
          {substanceId && substanceById.get(substanceId)?.scopeNote && <p className="text-xs text-ink-500">범위: {substanceById.get(substanceId)!.scopeNote}</p>}
          <TextArea label="연결 근거(원문 문구)" value={basisNote} onChange={setBasisNote} placeholder="예: 고시 「벤젠」 = 별표 21 1-가-43 「벤젠(Benzene; 71-43-2)」 — 문구 동일" />
          <TextArea label="변경 사유" value={reason} onChange={setReason} placeholder="왜 지금 바꾸나 — 이력에 남습니다" />
        </div>
      </Modal>

      {/* 연결 해제(49종 · 별표 21) */}
      <Modal
        open={modal?.kind === 'removeEntry' || modal?.kind === 'removeHazard'}
        onClose={close}
        title="연결 해제"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={busy}>
              취소
            </Button>
            <Button
              variant="danger"
              disabled={busy || !reason.trim() || (modal?.kind === 'removeEntry' && modal.entry.links.length <= 1 && nextStatus === 'UNRESOLVED' && !note.trim())}
              onClick={() => (modal?.kind === 'removeEntry' ? removeEntry.mutate(modal) : modal?.kind === 'removeHazard' ? removeHazard.mutate(modal) : undefined)}
            >
              해제
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {modal?.kind === 'removeEntry' && (
            <p className="text-sm text-ink-700">
              {modal.entry.itemCode} {modal.entry.text} ↔ {substanceById.get(modal.substanceId)?.nameKo ?? modal.substanceId}
            </p>
          )}
          {modal?.kind === 'removeHazard' && (
            <p className="text-sm text-ink-700">
              {modal.hazard.code} {modal.hazard.name} ↔ {substanceById.get(modal.substanceId)?.nameKo ?? modal.substanceId}
            </p>
          )}
          <TextArea label="변경 사유" value={reason} onChange={setReason} />
          {modal?.kind === 'removeEntry' && modal.entry.links.length <= 1 && (
            <>
              <p className="text-xs text-amber-700">마지막 연결입니다 — 푼 뒤 항목 상태를 정해 주세요(연결 0인 채 「연결됨」으로 둘 수 없습니다).</p>
              <label className="block">
                <span className={FORM_LABEL_CLASS}>다음 상태</span>
                <select className={FORM_CONTROL_CLASS} value={nextStatus} onChange={(e) => setNextStatus(e.target.value as RegulatoryListEntryLinkStatusValue)} aria-label="다음 상태">
                  <option value="UNRESOLVED">{REGULATORY_LIST_ENTRY_LINK_STATUS_LABEL.UNRESOLVED}</option>
                  <option value="UNREVIEWED">{REGULATORY_LIST_ENTRY_LINK_STATUS_LABEL.UNREVIEWED}</option>
                </select>
              </label>
              {nextStatus === 'UNRESOLVED' && <TextArea label="미확정 이유" value={note} onChange={setNote} />}
            </>
          )}
        </div>
      </Modal>

      {/* 상태 변경 */}
      <Modal
        open={modal?.kind === 'status'}
        onClose={close}
        title={modal?.kind === 'status' ? `${modal.entry.itemCode} ${modal.entry.text} — 상태` : ''}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={busy}>
              취소
            </Button>
            <Button variant="primary" disabled={busy || !reason.trim() || (nextStatus === 'UNRESOLVED' && !note.trim())} onClick={() => (modal?.kind === 'status' ? setStatus.mutate(modal) : undefined)}>
              저장
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <label className="block">
            <span className={FORM_LABEL_CLASS}>상태</span>
            <select className={FORM_CONTROL_CLASS} value={nextStatus} onChange={(e) => setNextStatus(e.target.value as RegulatoryListEntryLinkStatusValue)} aria-label="상태">
              {(['LINKED', 'UNRESOLVED', 'UNREVIEWED'] as const).map((s) => (
                <option key={s} value={s} disabled={s === 'LINKED' && modal?.kind === 'status' && modal.entry.links.length === 0}>
                  {REGULATORY_LIST_ENTRY_LINK_STATUS_LABEL[s]}
                  {s === 'LINKED' && modal?.kind === 'status' && modal.entry.links.length === 0 ? '(연결이 없어 고를 수 없음)' : ''}
                </option>
              ))}
            </select>
          </label>
          {nextStatus === 'UNRESOLVED' && <TextArea label="미확정 이유(검토했으나 근거 없음 — 무엇을 봤나)" value={note} onChange={setNote} />}
          <TextArea label="변경 사유" value={reason} onChange={setReason} />
        </div>
      </Modal>

      {/* 이력 */}
      <Modal open={modal?.kind === 'history'} onClose={close} title={modal?.kind === 'history' ? modal.title : ''} size="lg">
        {historyQuery.isLoading ? (
          <p className="text-sm text-ink-500">불러오는 중…</p>
        ) : historyQuery.isError ? (
          // 실패를 「이력이 없습니다」로 그리면 이력이 «없다»는 거짓 사실이 된다(리뷰 1103).
          <p role="alert" className="text-sm text-red-600">
            {apiErrorMessage(historyQuery.error, '이력을 불러오지 못했습니다.')}
          </p>
        ) : (historyQuery.data ?? []).length === 0 ? (
          <p className="text-sm text-ink-500">이력이 없습니다.</p>
        ) : (
          <ul className="divide-y divide-line text-sm" data-testid="chemical-history">
            {(historyQuery.data ?? []).map((h) => (
              <li key={h.id} className="py-2">
                <p>
                  <span className="font-mono text-xs text-ink-500">{formatDateTimeISO(new Date(h.changedAt))}</span> · {CHEMICAL_LINK_ACTION_LABEL[h.action]}
                  {h.substanceId ? ` · ${substanceById.get(h.substanceId)?.nameKo ?? h.substanceId}` : ''}
                  {h.statusBefore || h.statusAfter
                    ? ` · ${h.statusBefore ? REGULATORY_LIST_ENTRY_LINK_STATUS_LABEL[h.statusBefore] : '—'} → ${h.statusAfter ? REGULATORY_LIST_ENTRY_LINK_STATUS_LABEL[h.statusAfter] : '—'}`
                    : ''}
                  {h.changedBy ? ` · ${h.changedBy}` : ''}
                </p>
                <p className="text-xs text-ink-600">사유: {h.reason}</p>
                {h.basisNote && <p className="text-xs text-ink-500">근거: {h.basisNote}</p>}
                {h.noteAfter && <p className="text-xs text-ink-500">미확정 이유: {h.noteAfter}</p>}
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </div>
  );
}
